// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useRef, useState, type FormEvent } from 'react';

import { authApi } from '../api/auth';
import {
  LOGIN_LOOP_TEXT,
  loginErrorText,
  loginLinkErrorText,
  loginLinkLooksSent,
  loginLinkRedirect,
  safeNext,
} from '../app/auth';
import { CASES_PATH } from '../app/cases';
import { clearLoginHops, recordLoginHop, sessionHopStore } from '../app/loginLoop';
import { sessionBridge } from '../app/sessionRuntime';
import { useMutationQueue } from '../app/useMutationQueue';
import { Button } from '../components/Button';
import controls from '../components/controls.module.css';
import { Logo } from '../components/Logo';
import { TextInput } from '../components/TextInput';
import { ThemeToggle } from '../components/ThemeToggle';
import { useTheme } from '../app/useTheme';
import { runtimeConfig } from '../config';
import styles from './LoginPage.module.css';

/**
 * Log ind — the only screen a signed-out browser sees (ruxd server mode).
 * The account lives in Supabase: the form signs in there, then trades the
 * token for ruxd's session cookie (the bridge, `app/session.ts`) and goes on
 * to `?next=` (a path on this server) or the case list. With login links
 * switched on in `/config.json` it can also mail one; the link comes back
 * here and the same bridge finishes the job. Local mode never shows it:
 * someone already signed in, or a local server, skips straight on.
 */
export function LoginPage() {
  const theme = useTheme();
  const bridge = sessionBridge();
  const magicLink = bridge !== null && runtimeConfig().supabase?.magicLink === true;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [linkSent, setLinkSent] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const emailRef = useRef<HTMLInputElement>(null);
  const next = safeNext(new URLSearchParams(window.location.search).get('next'));

  // Already signed in — to ruxd, or to Supabase (a login link just came
  // back, or ruxd's session ran out first) — or a local server: no form.
  // Forwarding is guarded: a browser that keeps bouncing back here waits
  // longer each time and then stops, with the reason (`loginLoop.ts`).
  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const showForm = (reason?: string) => {
      if (controller.signal.aborted) return;
      if (reason) setFailure(reason);
      setChecking(false);
      requestAnimationFrame(() => emailRef.current?.focus());
    };
    const forward = () => {
      if (controller.signal.aborted) return;
      const hop = recordLoginHop(sessionHopStore(), Date.now());
      if (hop.stop) return showForm(LOGIN_LOOP_TEXT);
      timer = setTimeout(() => window.location.replace(next), hop.delayMs);
    };
    authApi.me(controller.signal).then(forward, () => {
      if (!bridge) return showForm();
      bridge.rebridge().then(
        (me) => (me ? forward() : showForm()),
        (cause: unknown) => showForm(loginErrorText(cause)),
      );
    });
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [next, bridge]);

  const { busy, mutate } = useMutationQueue({
    scope: 'page',
    onError: (cause) => {
      setPassword('');
      setFailure(loginErrorText(cause));
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy || !bridge) return;
    setFailure(null);
    setLinkSent(null);
    void mutate(async () => {
      await bridge.signIn(email.trim(), password);
      clearLoginHops(sessionHopStore());
      window.location.replace(next);
    });
  };

  const sendLink = () => {
    const address = email.trim();
    if (busy || !bridge || address === '') return;
    setFailure(null);
    setLinkSent(null);
    void mutate(async () => {
      try {
        await bridge.sendLoginLink(address, loginLinkRedirect(window.location.origin, next));
      } catch (cause) {
        if (!loginLinkLooksSent(cause)) {
          setFailure(loginLinkErrorText(cause));
          return;
        }
      }
      setLinkSent(address);
    });
  };

  return (
    <div className={styles.page}>
      <div className={styles.theme}>
        <ThemeToggle preference={theme.preference} setPreference={theme.setPreference} />
      </div>
      <main className={styles.card} aria-busy={checking}>
        <div className={styles.brand}>
          <Logo size={28} playOnMount />
          <span className={styles.tagline}>Ressourcekortlægning af bygninger</span>
        </div>
        <h1 className={styles.title}>Log ind</h1>
        {!bridge ? (
          <p className={styles.notice} role="status">
            {checking
              ? 'Tjekker login…'
              : 'Login er ikke sat op på denne server: den mangler Supabase-oplysningerne i config.json. Kontakt serverens administrator.'}
          </p>
        ) : (
          <form className={styles.form} onSubmit={submit} noValidate>
            <label className={controls.field}>
              <span className={controls.fieldLabel}>E-mail</span>
              <TextInput
                ref={emailRef}
                variant="outline"
                size="lg"
                block
                className={styles.input}
                type="email"
                name="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={checking}
                required
              />
            </label>
            <label className={controls.field}>
              <span className={controls.fieldLabel}>Adgangskode</span>
              <TextInput
                variant="outline"
                size="lg"
                block
                className={styles.input}
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={checking}
                required
              />
            </label>
            {failure && (
              <p className={styles.error} role="alert">
                {failure}
              </p>
            )}
            {linkSent && (
              <p className={styles.notice} role="status">
                Hvis {linkSent} har en konto, er der sendt et login-link. Åbn det i denne browser.
              </p>
            )}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              block
              className={styles.submit}
              disabled={checking || busy || email.trim() === '' || password === ''}
            >
              {busy ? 'Logger ind…' : 'Log ind'}
            </Button>
            {magicLink && (
              <>
                <div className={styles.divider} aria-hidden="true">
                  <span>eller</span>
                </div>
                <Button
                  variant="outline"
                  size="lg"
                  block
                  className={styles.secondary}
                  disabled={checking || busy || email.trim() === ''}
                  onClick={sendLink}
                >
                  Send login-link til e-mailen
                </Button>
              </>
            )}
          </form>
        )}
        <p className={styles.help}>
          {magicLink
            ? 'Har du ingen konto? Kontakt serverens administrator. Har du glemt adgangskoden, så brug et login-link.'
            : 'Har du ingen konto, eller har du glemt adgangskoden? Kontakt serverens administrator.'}
        </p>
        {next !== CASES_PATH && <p className={styles.help}>Du sendes videre til den side, du var på.</p>}
      </main>
    </div>
  );
}
